import { useState } from 'react'
import cn from 'classnames'
import { useAppSelector } from 'redux/hooks'
import { Button } from 'react-bootstrap'
import { MAX_RADIUS, MIN_RADIUS } from 'redux/features/map/slice'
import ReactLoading from 'react-loader-spinner'
import { IoIosArrowDown, IoIosArrowUp } from 'react-icons/io'
import { TypeBien } from 'interfaces/API'
import AutocompleteInsee from 'components/AutocompleteInsee/AutocompleteInsee'
import RangeSliderDistance from 'components/RangeSlider/RangeSliderDistance/RangeSliderDistance'
import ToggleSwitch from 'components/ToggleSwitch'
import Label from 'components/Label'
import InputNumber from 'components/InputNumber'
import Checkbox from 'components/Checkbox'
import useBienAVendre from 'hooks/useBienAVendre'
import useSearchCircle from 'hooks/useSearchCircle'
import { Status as StatusParcelle } from 'redux/features/parcelle/slice'
import { makeSelectParcelles } from 'redux/features/parcelle/selectors'

import useToggle from 'hooks/useToggle'
import { getTooltip, Tooltip } from 'utils/tooltipLibrary'

import useUtilisateur from 'hooks/useUtilisateur'
import formatNumber from 'utils/formatNumber'

import s from '../../../../common.module.scss'
import InputEmpriseAuSol from '../../InputEmpriseAuSol'
import InputSurfaceTerrain from '../../InputSurfaceTerrain'
import BiensAVendreNonAutorise from '../../../../BiensAVendreNonAutorise/BienAVendreNonAutorise'
import GarageCalculator from './components/GarageCalculator'
import EtageCalculator from './components/EtageCalculator/EtageCalculator'

const FormulaireV2 = (): JSX.Element => {
  const {
    locations,
    typeBien,
    surfaceHabitable,
    surfaceTerrainMin,
    surfaceTerrainMax,
    surfaceAuSolMin,
    surfaceAuSolMax,
    batiMitoyen,
    piscine,
    doNotUseSurfaceHabitable,
    setLocation,
    setTypeDeBien,
    setPiscine,
    setSurfaceHabitable,
    toggleDoNotUseSurfaceHabitable,
    setSurfaceAuSolMin,
    setSurfaceAuSolMax,
    setSurfaceTerrainMin,
    setSurfaceTerrainMax,
    setBatiMitoyen,
    onSubmitForm,
    statusParcelle,
    onNouveauCiblageClick,
    onEffacerCriteresClick,
  } = useBienAVendre()
  const {
    searchCircle,
    computeSearchCircle,
    resetSearchCircle,
    setSearchCircleRadius,
  } = useSearchCircle()
  const parcelles = useAppSelector(makeSelectParcelles)
  const [toggleSearchMode, setToggleSearchMode] = useToggle()
  const { checkUtilisateurAutorise } = useUtilisateur()
  const [showCritereAvance, setShowCritereAvance] = useState(false)
  const [activeTypeDeBien, setActiveTypeDeBien] = useState(TypeBien.Maison)

  const handleToggleLocalisation = (newValue: boolean) => {
    setToggleSearchMode(newValue)
    if (newValue) {
      computeSearchCircle()
    } else {
      resetSearchCircle()
    }
  }

  const handleMitoyenChange = () => {
    setBatiMitoyen(batiMitoyen === 2 ? 0 : 2)
  }

  const handlePiscineChange = () => {
    setPiscine(piscine === 2 ? 0 : 2)
  }

  if (!checkUtilisateurAutorise()) {
    return <BiensAVendreNonAutorise />
  }

  const handleToggleCritereAvance = () => {
    setShowCritereAvance(!showCritereAvance)
  }

  const handleTypeDeBien = (type: TypeBien) => {
    setTypeDeBien(type)
    setActiveTypeDeBien(type)
  }

  return (
    <div className={s.formulaire}>
      <div className={s.localisation}>
        <div className={s.flexBetween}>
          <Label
            label="Localisation"
            tooltip={getTooltip(Tooltip.Localisation)}
          />
          <Button
            className={s.underline}
            variant="link"
            onClick={onEffacerCriteresClick}
          >
            Effacer les critères
          </Button>
        </div>
        <ToggleSwitch
          className={s.toggleSwitch}
          value={toggleSearchMode}
          onChange={handleToggleLocalisation}
          labelLeft="Localiser par ville(s)"
          labelRight="Localiser avec un rayon"
        />
        {!toggleSearchMode && (
          <>
            <AutocompleteInsee
              locations={locations}
              setLocations={setLocation}
            />
            <p className={s.helpInputText}>
              * Champ requis pour lancer la recherche
            </p>
          </>
        )}
        {toggleSearchMode && (
          <>
            <p>
              Déplacez-vous sur la carte pour modifier la zone de votre rayon.
              <br />
              Puis choisissez l&apos;étendue de votre rayon ci-dessous :
            </p>
            {searchCircle && (
              <RangeSliderDistance
                min={MIN_RADIUS}
                max={MAX_RADIUS}
                value={searchCircle.radius}
                onChange={setSearchCircleRadius}
              />
            )}
          </>
        )}
      </div>
      <div className={s.typeDeBienV2}>
        <div className={s.label}>
          <Label
            label="Type de bien : "
            tooltip={getTooltip(Tooltip.TypeDeBien)}
          />
        </div>
        <div className={s.filterToggle}>
          <button
            type="button"
            className={cn(
              s.buttonToggle,
              activeTypeDeBien === TypeBien.Maison ? s.active : ''
            )}
            onClick={() => handleTypeDeBien(TypeBien.Maison)}
            onKeyPress={() => handleTypeDeBien(TypeBien.Maison)}
            tabIndex={0}
          >
            Maison
          </button>
          <button
            type="button"
            className={cn(
              s.buttonToggle,
              activeTypeDeBien === TypeBien.Terrain ? s.active : ''
            )}
            onClick={() => handleTypeDeBien(TypeBien.Terrain)}
            onKeyPress={() => handleTypeDeBien(TypeBien.Terrain)}
            tabIndex={0}
          >
            Terrain
          </button>
        </div>
        <p className={s.helpInputText}>
          * Champ requis pour lancer la recherche
        </p>
      </div>
      <br />
      <div className={s.surfaceTerrainBienAVendre}>
        <InputSurfaceTerrain
          tooltip={getTooltip(Tooltip.SurfaceTerrain)}
          leftInput={{
            value: formatNumber(surfaceTerrainMin),
            onChange: setSurfaceTerrainMin,
          }}
          rightInput={{
            value: formatNumber(surfaceTerrainMax),
            onChange: setSurfaceTerrainMax,
          }}
        />
      </div>
      {activeTypeDeBien === TypeBien.Maison && (
        <div
          role="button"
          tabIndex={0}
          className={cn(
            s.simpleButtonWithChevron,
            s.blockAfficherCritereAvance
          )}
          onClick={() => {
            handleToggleCritereAvance()
          }}
          onKeyPress={() => {
            handleToggleCritereAvance()
          }}
        >
          {showCritereAvance ? (
            <span>
              Masquer les critères avancés <IoIosArrowUp />
            </span>
          ) : (
            <span>
              Afficher les critères avancés <IoIosArrowDown />
            </span>
          )}
        </div>
      )}
      {typeBien === TypeBien.Maison && (
        <div>
          {showCritereAvance && (
            <>
              <div className={s.caracteristiques}>
                <Label
                  label="Caractéristiques : "
                  tooltip={getTooltip(Tooltip.CaracteristiquesMaison)}
                />

                <div>
                  <button
                    type="button"
                    id="bienAVendre-piscine"
                    onClick={() => handlePiscineChange()}
                    value={piscine === null ? 2 : piscine}
                    className={cn(
                      s.selectFilterButton,
                      piscine === 0 ? s.active : ''
                    )}
                  >
                    Piscine
                  </button>
                  <button
                    type="button"
                    id="bienAVendre-batiMitoyen"
                    onClick={() => handleMitoyenChange()}
                    value={batiMitoyen === null ? 2 : batiMitoyen}
                    className={cn(
                      s.selectFilterButton,
                      batiMitoyen === 0 ? s.active : ''
                    )}
                  >
                    Maison mitoyenne
                  </button>
                </div>
              </div>

              <div className={s.surfaceHabitableAnnonce}>
                <Label
                  label="Surface habitable indiquée dans l'annonce : "
                  tooltip={getTooltip(Tooltip.SurfaceHabitableAnnonce)}
                />
                <InputNumber
                  value={formatNumber(surfaceHabitable)}
                  onChange={setSurfaceHabitable}
                  disabled={doNotUseSurfaceHabitable}
                />
                <span>m²</span>
                <Checkbox
                  checked={doNotUseSurfaceHabitable}
                  onChange={toggleDoNotUseSurfaceHabitable}
                  label="Ne pas utiliser ce critère dans ma recherche"
                />
              </div>

              <div className={s.estimationEmpriseAuSolSection}>
                <Label
                  label="Estimation de l'emprise au sol :"
                  tooltip={getTooltip(
                    Tooltip.FormeParticuliereOuGarageAttenant
                  )}
                />
                <GarageCalculator />
                <EtageCalculator />
              </div>
              <div className={s.empriseSurface}>
                <InputEmpriseAuSol
                  tooltip={getTooltip(Tooltip.EmpriseAuSol)}
                  leftInput={{
                    value: formatNumber(surfaceAuSolMin),
                    onChange: setSurfaceAuSolMin,
                  }}
                  rightInput={{
                    value: formatNumber(surfaceAuSolMax),
                    onChange: setSurfaceAuSolMax,
                  }}
                />
              </div>
            </>
          )}
        </div>
      )}
      <div className={s.sectionFooter}>
        {statusParcelle === StatusParcelle.ShowParcelleResults &&
          !parcelles.length && (
            <p className={s.noResult}>
              Aucun résultat ne correspond à votre recherche.
            </p>
          )}
        <Button
          id="page-bien-a-vendre-btn-rechercher"
          className={cn('mb-4', s.buttonRechercher)}
          variant="primary"
          size="lg"
          disabled={statusParcelle === StatusParcelle.Pending}
          onClick={onSubmitForm}
        >
          {statusParcelle === StatusParcelle.Pending ? (
            <ReactLoading type="Oval" color="#fff" height="2.4rem" />
          ) : (
            'Rechercher'
          )}
        </Button>
        <Button
          className={s.underline}
          variant="link"
          onClick={onNouveauCiblageClick}
        >
          Nouveau ciblage
        </Button>
      </div>
    </div>
  )
}

export default FormulaireV2
